import { supabase } from '@/lib/supabase';
import { Interview } from '@/types/models';
import { Database } from '@/types/database.types';
import { ServiceResponse, ServiceArrayResponse, handleServiceError, toCamelCase, toSnakeCase } from './baseService';
import { emailService } from './emailService';

type InterviewRow = Database['public']['Tables']['interviews']['Row']
type InterviewInsert = Database['public']['Tables']['interviews']['Insert']
type InterviewUpdate = Database['public']['Tables']['interviews']['Update']

export const interviewService = {
  /**
   * Get interviews with optional filters
   */
  async getInterviews(filters?: {
    status?: string;
    scheduledAfter?: string;
    scheduledBefore?: string;
    organizationId?: string;
    interviewerId?: string;
    candidateId?: string;
  }): Promise<Interview[]> {
    try {
      let query = supabase
        .from('interviews')
        .select('*')
        .order('scheduled_at', { ascending: true });

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.scheduledAfter) {
        query = query.gte('scheduled_at', filters.scheduledAfter);
      }
      if (filters?.scheduledBefore) {
        query = query.lte('scheduled_at', filters.scheduledBefore);
      }
      if (filters?.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }
      if (filters?.interviewerId) {
        query = query.eq('interviewer_id', filters.interviewerId);
      }
      if (filters?.candidateId) {
        query = query.eq('candidate_id', filters.candidateId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []).map(row => toCamelCase<Interview>(row));
    } catch (error) {
      console.error('Error fetching interviews:', error);
      return [];
    }
  },

  async getByOrganizationId(organizationId: string): Promise<ServiceArrayResponse<Interview>> {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('organization_id', organizationId)
        .order('scheduled_at', { ascending: true })

      if (error) throw error
      return { data: (data || []).map(row => toCamelCase<Interview>(row)), error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  async getByInterviewerId(interviewerId: string): Promise<ServiceArrayResponse<Interview>> {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('interviewer_id', interviewerId)
        .order('scheduled_at', { ascending: true })

      if (error) throw error
      return { data: (data || []).map(row => toCamelCase<Interview>(row)), error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  async getByCandidateId(candidateId: string): Promise<ServiceArrayResponse<Interview>> {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('scheduled_at', { ascending: true })

      if (error) throw error
      return { data: (data || []).map(row => toCamelCase<Interview>(row)), error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  async getById(id: string): Promise<ServiceResponse<Interview>> {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return { data: data ? toCamelCase<Interview>(data) : null, error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  /**
   * Create a new interview and send confirmation emails
   */
  async createInterview(interview: Omit<Interview, 'id' | 'createdAt' | 'updatedAt'>): Promise<Interview> {
    const { data, error } = await supabase
      .from('interviews')
      .insert([toSnakeCase(interview)])
      .select()
      .single();

    if (error) throw error;

    const createdInterview = toCamelCase(data) as Interview;

    // Send confirmation emails asynchronously
    this.sendInterviewNotifications(createdInterview).catch(err => {
      console.error('Failed to send interview notifications:', err);
    });

    return createdInterview;
  },

  /**
   * Update interview and send notifications if status changed
   */
  async updateInterview(id: string, updates: Partial<Interview>): Promise<Interview> {
    const { data, error } = await supabase
      .from('interviews')
      .update(toSnakeCase(updates))
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    const updatedInterview = toCamelCase(data) as Interview;

    // Send notifications if status changed
    if (updates.status && updates.status !== 'requested') {
      this.sendStatusUpdateNotifications(updatedInterview, updates.status).catch(err => {
        console.error('Failed to send status update notifications:', err);
      });
    }

    return updatedInterview;
  },

  /**
   * Send interview confirmation and invitation emails
   */
  async sendInterviewNotifications(interview: Interview): Promise<void> {
    try {
      // Fetch related data
      const [candidate, interviewer, job, organization] = await Promise.all([
        this.fetchCandidate(interview.candidateId),
        this.fetchInterviewer(interview.interviewerId),
        this.fetchJob(interview.jobId),
        this.fetchOrganization(interview.organizationId)
      ]);

      if (!candidate || !interviewer || !job || !organization) {
        console.error('Missing required data for email notifications');
        return;
      }

      // Format scheduled time
      const scheduledAt = new Date(interview.scheduledAt).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: candidate.timezone || 'UTC'
      });

      // Send confirmation to candidate
      await emailService.sendInterviewConfirmation(candidate.email, {
        candidateName: candidate.fullName,
        jobTitle: job.title,
        organizationName: organization.name,
        interviewType: interview.interviewType,
        scheduledAt,
        duration: interview.durationMinutes,
        interviewerName: interviewer.fullName,
        meetingLink: interview.meetingLink,
        meetingPassword: interview.meetingPassword
      });

      // Send invitation to interviewer
      await emailService.sendInterviewerInvitation(interviewer.email, {
        interviewerName: interviewer.fullName,
        jobTitle: job.title,
        organizationName: organization.name,
        candidateName: candidate.fullName,
        scheduledAt,
        duration: interview.durationMinutes,
        interviewType: interview.interviewType,
        acceptLink: `${typeof window !== 'undefined' ? window.location.origin : ''}/interviewer-dashboard`
      });

    } catch (error) {
      console.error('Error sending interview notifications:', error);
    }
  },

  /**
   * Send interview reminders (called by scheduled task)
   */
  async sendInterviewReminders(interviewId: string): Promise<void> {
    try {
      const result = await this.getById(interviewId);
      const interview = result.data;
      if (!interview || interview.status !== 'scheduled') return;

      const [candidate, interviewer, job] = await Promise.all([
        this.fetchCandidate(interview.candidateId),
        this.fetchInterviewer(interview.interviewerId),
        this.fetchJob(interview.jobId)
      ]);

      if (!candidate || !interviewer || !job) return;

      const scheduledAt = new Date(interview.scheduledAt).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Send reminder to candidate
      await emailService.sendInterviewReminder(candidate.email, {
        recipientName: candidate.fullName,
        jobTitle: job.title,
        scheduledAt,
        duration: interview.durationMinutes,
        role: 'candidate',
        otherPartyName: interviewer.fullName,
        meetingLink: interview.meetingLink
      });

      // Send reminder to interviewer
      await emailService.sendInterviewReminder(interviewer.email, {
        recipientName: interviewer.fullName,
        jobTitle: job.title,
        scheduledAt,
        duration: interview.durationMinutes,
        role: 'interviewer',
        otherPartyName: candidate.fullName,
        meetingLink: interview.meetingLink
      });

    } catch (error) {
      console.error('Error sending interview reminders:', error);
    }
  },

  /**
   * Send status update notifications
   */
  async sendStatusUpdateNotifications(interview: Interview, newStatus: string): Promise<void> {
    // Implementation for status change notifications
    // This would include cancellation, rescheduling, completion notifications
  },

  /**
   * Helper methods to fetch related data
   */
  async fetchCandidate(candidateId: string) {
    const { data } = await supabase
      .from('candidates')
      .select('*, user:user_profiles(*)')
      .eq('id', candidateId)
      .single();
    
    return data ? {
      ...toCamelCase(data),
      email: data.user?.email || data.email,
      fullName: data.user?.full_name || data.full_name,
      timezone: data.user?.timezone
    } : null;
  },

  async fetchInterviewer(interviewerId: string) {
    const { data } = await supabase
      .from('interviewer_profiles')
      .select('*, user:user_profiles(*)')
      .eq('id', interviewerId)
      .single();
    
    return data ? {
      ...toCamelCase(data),
      email: data.user?.email,
      fullName: data.user?.full_name
    } : null;
  },

  async fetchJob(jobId: string) {
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single();
    
    return data ? toCamelCase(data) : null;
  },

  async fetchOrganization(organizationId: string) {
    const { data } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single();
    
    return data ? toCamelCase(data) : null;
  },

  async create(interview: Omit<Interview, 'id' | 'createdAt' | 'updatedAt'>): Promise<ServiceResponse<Interview>> {
    try {
      const insertData: InterviewInsert = toSnakeCase(interview)
      const { data, error } = await supabase
        .from('interviews')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error
      return { data: data ? toCamelCase<Interview>(data) : null, error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  async update(id: string, updates: Partial<Interview>): Promise<ServiceResponse<Interview>> {
    try {
      const updateData: InterviewUpdate = toSnakeCase(updates)
      const { data, error } = await supabase
        .from('interviews')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { data: data ? toCamelCase<Interview>(data) : null, error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },

  async delete(id: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await supabase
        .from('interviews')
        .delete()
        .eq('id', id)

      if (error) throw error
      return { data: null, error: null }
    } catch (error) {
      return { data: null, error: handleServiceError(error) }
    }
  },
}